import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { links } from "./SuperAdminPages";
import api from "../../lib/api";
import { toast } from "sonner";
import { Download, RefreshCw, FileBarChart, Wallet, Building2, Users, Activity, Sparkles, Calendar } from "lucide-react";

const PERIODS = [
  { v: "today", label: "Today" },
  { v: "7d", label: "Last 7d" },
  { v: "30d", label: "Last 30d" },
  { v: "this_month", label: "This month" },
  { v: "last_month", label: "Last month" },
  { v: "this_year", label: "This year" },
];

const TABS = [
  { key: "saas", label: "SaaS Revenue", icon: Wallet },
  { key: "clinics", label: "Clinics", icon: Building2 },
  { key: "users", label: "Users", icon: Users },
  { key: "usage", label: "Usage", icon: Activity },
  { key: "ai", label: "AI Usage", icon: Sparkles },
  { key: "renewals", label: "Renewals", icon: Calendar },
];

export default function SuperAdminReportsPage() {
  const [tab, setTab] = useState("saas");
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ep = {
        saas: `/admin/reports/saas-revenue?period=${period}`,
        clinics: "/admin/reports/clinic-summary",
        users: "/admin/reports/user-summary",
        usage: `/admin/reports/usage?period=${period}`,
        ai: "/admin/reports/ai-usage",
        renewals: "/admin/reports/renewals",
      }[tab];
      const r = await api.get(ep);
      setData((d) => ({ ...d, [tab]: r.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  }, [period, tab]);

  useEffect(() => { load(); }, [load]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data[tab] || {}, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `medigenie-report-${tab}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
  };

  return (
    <DashboardLayout links={links} title="Reports">
      <div className="mg-card mb-4">
        <div className="flex items-center gap-2 flex-wrap" data-testid="reports-toolbar">
          {PERIODS.map((p) => (
            <button key={p.v} onClick={() => setPeriod(p.v)}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border transition ${period === p.v ? "bg-mg-medical text-white border-mg-medical" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              data-testid={`report-period-${p.v}`}>{p.label}</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={load} className="mg-btn-secondary text-sm" data-testid="report-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={exportJson} className="mg-btn-secondary text-sm" data-testid="report-export">
              <Download className="w-4 h-4" /> Export
            </button>
          </div>
        </div>
      </div>

      <div className="mg-card overflow-x-auto p-0 mb-4">
        <div className="flex border-b border-slate-100">
          {TABS.map((t) => {
            const I = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === t.key ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}
                data-testid={`report-tab-${t.key}`}>
                <I className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "saas" && <SaasRevenue data={data.saas} />}
      {tab === "clinics" && <ClinicSummary data={data.clinics} />}
      {tab === "users" && <UserSummary data={data.users} />}
      {tab === "usage" && <UsageSummary data={data.usage} />}
      {tab === "ai" && <AiSummary data={data.ai} />}
      {tab === "renewals" && <RenewalsSummary data={data.renewals} />}
    </DashboardLayout>
  );
}

function CardKv({ label, value, hint, testid }) {
  return (
    <div className="mg-card" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-2xl font-extrabold text-mg-navy mt-1">{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function SaasRevenue({ data }) {
  if (!data) return <Loading />;
  return (
    <>
      <div className="mb-4">
        <CardKv label="Total MRR" value={`₹${(data.total_mrr || 0).toLocaleString()}`} testid="kv-total-mrr" />
      </div>
      <div className="mg-card overflow-x-auto p-0" data-testid="saas-revenue-table">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="px-3 py-3">Plan</th><th>Visibility</th><th>Monthly price</th><th>Subscriptions</th><th className="text-right">MRR</th></tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 && <tr><td colSpan="5" className="px-3 py-8 text-center text-slate-400">No data.</td></tr>}
            {(data.rows || []).map((r) => (
              <tr key={r.plan_id} className="border-b border-slate-50">
                <td className="px-3 py-2 font-semibold text-mg-navy">{r.plan_name}</td>
                <td className="text-xs">{r.visibility}</td>
                <td className="text-xs">₹{(r.monthly_price || 0).toLocaleString()}</td>
                <td className="text-xs">{r.subscriptions}</td>
                <td className="text-right font-bold text-mg-medical">₹{r.mrr.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ClinicSummary({ data }) {
  if (!data) return <Loading />;
  return (
    <div className="space-y-4" data-testid="clinic-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CardKv label="Total clinics" value={data.total} />
        <CardKv label="Deleted" value={data.deleted} />
      </div>
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2 text-sm">By subscription status</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(data.by_subscription_status || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-slate-50 py-1.5 text-sm">
              <span className="text-slate-600 capitalize">{k}</span><span className="font-bold text-mg-navy">{v}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2 text-sm">By plan</h3>
        <div className="space-y-1">
          {(data.by_plan || []).map((p) => (
            <div key={p.plan_id} className="flex justify-between border-b border-slate-50 py-1.5 text-sm">
              <span className="text-slate-600">{p.name}</span><span className="font-bold text-mg-navy">{p.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UserSummary({ data }) {
  if (!data) return <Loading />;
  return (
    <div className="space-y-4" data-testid="user-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CardKv label="Total users" value={data.total} />
        <CardKv label="Active" value={data.active} />
        <CardKv label="Inactive" value={data.inactive} />
        <CardKv label="Active (30d)" value={data.active_30d} />
      </div>
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2 text-sm">By role</h3>
        {Object.entries(data.by_role || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-slate-50 py-1.5 text-sm">
            <span className="text-slate-600 capitalize">{(k || "—").replace(/_/g, " ")}</span><span className="font-bold text-mg-navy">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsageSummary({ data }) {
  if (!data) return <Loading />;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="usage-summary">
      <CardKv label="Appointments" value={data.appointments} />
      <CardKv label="Patients added" value={data.patients_added} />
      <CardKv label="Doctors added" value={data.doctors_added} />
      <CardKv label="Invoices" value={data.invoices} />
    </div>
  );
}

function AiSummary({ data }) {
  if (!data) return <Loading />;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ai-summary">
      <CardKv label="AI-enabled clinics" value={data.enabled_clinics} />
      <CardKv label="Total tokens used" value={(data.tokens_total || 0).toLocaleString()} />
    </div>
  );
}

function RenewalsSummary({ data }) {
  if (!data) return <Loading />;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="renewals-summary">
      <CardKv label="Upcoming (7d)" value={data.upcoming_7d} testid="kv-up-7" />
      <CardKv label="Upcoming (15d)" value={data.upcoming_15d} testid="kv-up-15" />
      <CardKv label="Upcoming (30d)" value={data.upcoming_30d} testid="kv-up-30" />
      <CardKv label="Overdue" value={data.overdue} testid="kv-overdue" />
      <CardKv label="Trials ending" value={data.trials_ending} testid="kv-trials-ending" />
    </div>
  );
}

function Loading() { return <div className="mg-card text-center text-slate-400 py-8">Loading…</div>; }
